/**
 * Helper function to evaluate comparison operator
 * @param {number} value - Value to compare
 * @param {string} operator - Comparison operator: '>', '<', '>=', '<='
 * @param {number} threshold - Threshold value
 * @returns {boolean} - Result of comparison
 */
const evaluateOperator = (value, operator, threshold) => {
  switch (operator) {
    case '>': return value > threshold;
    case '<': return value < threshold;
    case '>=': return value >= threshold;
    case '<=': return value <= threshold;
    default: return value > threshold; // Default to >
  }
};

/**
 * Calculate severity level based on severity_index and threshold
 * @param {number} severityIndex - The severity index value from API
 * @param {number|Object} threshold - The threshold value (default: 9000) or object with {value, operator}
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateSeverityLevel = (severityIndex, threshold = 9000) => {
  const thresholdObj = typeof threshold === 'object' && threshold !== null ? threshold : { value: threshold, operator: '>=' };
  if (evaluateOperator(severityIndex, thresholdObj.operator, thresholdObj.value)) {
    return 'HIGH';
  } else if (evaluateOperator(severityIndex, '>=', thresholdObj.value * 0.5)) {
    return 'MEDIUM';
  } else {
    return 'LOW';
  }
};

/**
 * Get default threshold for an indicator
 * @param {string} indicatorName - Name of the indicator
 * @returns {number|Array|Object} - Default threshold value(s) with operators
 * For most indicators: [[highValue, highOp], [mediumValue, mediumOp], [lowValue, lowOp]]
 * For surface_temperature: { highNdvi: [value, op], highLst: [value, op], ... }
 */
export const getDefaultThreshold = (indicatorName) => {
  // Groundwater - single threshold
  if (indicatorName === 'Groundwater stress levels') {
    return { value: 0.5, operator: '>=' };
  }
  
  // Power outage - single threshold (legacy)
  if (indicatorName === 'Power outages (duration and geographic scope)' || indicatorName === 'Power Outage') {
    return { value: 9000, operator: '>=' };
  }
  
  // New indicators with 3 thresholds [[High value, High op], [Medium value, Medium op], [Low value, Low op]]
  const threeThresholdDefaults = {
    'Public transport disruptions (rail, metro, bus)': [[30, '>'], [30, '<'], [15, '<']], // disruption_minutes: High >30, Medium <30, Low <15
    'Road/bridge degradation or closures': [[2, '>'], [2, '<'], [1, '<']], // severity_index (days): High >2, Medium <2, Low <1
    'Traffic signal / equipment failures': [[30, '>'], [30, '<'], [5, '<']], // total_duration_minutes: High >30, Medium <30, Low <5
    'Cooling system failure in public buildings': [[45, '>'], [45, '<'], [5, '<']], // total_cooling_capacity_affected (minutes): High >45, Medium <45, Low <5
    'No. of school/college closure days due to heat': [[18, '>'], [18, '<'], [5, '<']], // total_schools_in_ward (days): High >18, Medium <18, Low <5
    '% of schools without basic cooling infrastructure': [[95, '>'], [95, '<'], [90, '<']], // percentage without cooling: High >95%, Medium <95%, Low <90%
    'Drop in per capita water availability (lpcd)': [[0.4, '>'], [0.4, '<'], [0.1, '<']], // drop_ratio: High >0.4, Medium <0.4, Low <0.1
    'Percentage of wards dependent on water tankers': [[0.4, '>'], [0.4, '<'], [0.1, '<']], // emergency_requests ratio: High >0.4, Medium <0.4, Low <0.1
    'Temperature differential (urban vs. rural)': [[6, '>'], [6, '<'], [2, '<']], // temp difference (deg C): High >6, Medium <6, Low <2
    'Homeless and street dwellers exposed': [[40, '>'], [40, '<'], [10, '<']], // occupancy_rate (%): High >40%, Medium <40%, Low <10%
  };
  
  if (threeThresholdDefaults[indicatorName]) {
    return threeThresholdDefaults[indicatorName];
  }
  
  // Surface temperature - dual thresholds (6 values with operators)
  if (indicatorName === 'Surface temperature & low vegetation zones') {
    return {
      highNdvi: [0.1, '<'],
      highLst: [45, '>'],
      mediumNdvi: [0.1, '>'],
      mediumLst: [45, '<'],
      lowNdvi: [0.5, '>'],
      lowLst: [40, '<']
    };
  }
  
  // Default fallback
  return { value: 9000, operator: '>=' };
};

/**
 * Classify groundwater severity from depletion_level_meters
 * @param {number} depletionLevelMeters - Depletion level in meters
 * @param {number} threshold - Threshold in meters (default 0.5)
 * @returns {string} - 'LOW' | 'MEDIUM' | 'HIGH'
 */
export const calculateGroundwaterSeverityLevel = (depletionLevelMeters, threshold = 0.5) => {
  if (depletionLevelMeters === 0) return 'LOW';
  if (depletionLevelMeters < threshold) return 'MEDIUM';
  return 'HIGH';
};

/**
 * Calculate severity level based on percentage value for informal workers indicators
 * Logic: High < 40%, Medium >= 40% and < 60%, Low >= 60%
 * @param {number} percentageValue - The percentage value (0-100)
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateInformalWorkersSeverityLevel = (percentageValue) => {
  if (percentageValue === null || percentageValue === undefined || isNaN(percentageValue)) {
    return 'LOW'; // Default to LOW if no data
  }
  
  const percentage = Number(percentageValue);
  
  if (percentage < 40) {
    return 'HIGH';
  } else if (percentage < 60) {
    return 'MEDIUM';
  } else {
    return 'LOW';
  }
};

/**
 * Calculate severity level for "Lost labor hours due to heat stress"
 * Logic: High < 20%, Medium >= 20% and < 80%, Low >= 80%
 * @param {number} percentageValue - The percentage value (0-100)
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateLostLaborHoursSeverityLevel = (percentageValue) => {
  if (percentageValue === null || percentageValue === undefined || isNaN(percentageValue)) {
    return 'LOW'; // Default to LOW if no data
  }
  
  const percentage = Number(percentageValue);
  
  if (percentage < 20) {
    return 'HIGH';
  } else if (percentage < 80) {
    return 'MEDIUM';
  } else {
    return 'LOW';
  }
};

/**
 * Calculate severity level for "% of workers without access to shade/water"
 * Logic: High < 20% (interpreted from "High < 80%" - assuming typo), Medium >= 20% and < 95%, Low >= 95%
 * Note: User specified "High < 80%, medium <95%, low >80%" which has overlap. 
 * Implementing as: High < 20% (best case), Medium 20-95%, Low >= 95% (worst case)
 * @param {number} percentageValue - The percentage value (0-100)
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateNoShadeWaterSeverityLevel = (percentageValue) => {
  if (percentageValue === null || percentageValue === undefined || isNaN(percentageValue)) {
    return 'LOW'; // Default to LOW if no data
  }
  
  const percentage = Number(percentageValue);
  
  // Interpreting user's "High < 80%, medium <95%, low >80%" as:
  // High if < 20% (best - few workers lack access), Medium if 20-95%, Low if >= 95% (worst - most workers lack access)
  if (percentage < 20) {
    return 'HIGH';
  } else if (percentage < 95) {
    return 'MEDIUM';
  } else {
    return 'LOW';
  }
};

/**
 * Calculate severity level for "Reported workplace heat illness cases"
 * Logic: High >= 80%, Medium >= 5% and < 80%, Low < 5%
 * @param {number} percentageValue - The percentage value (0-100)
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateHeatIllnessSeverityLevel = (percentageValue) => {
  if (percentageValue === null || percentageValue === undefined || isNaN(percentageValue)) {
    return 'LOW'; // Default to LOW if no data
  }
  
  const percentage = Number(percentageValue);
  
  if (percentage >= 80) {
    return 'HIGH';
  } else if (percentage >= 5) {
    return 'MEDIUM';
  } else {
    return 'LOW';
  }
};

/**
 * Calculate severity level for transport disruptions based on disruption_minutes
 * Logic: High > 30 minutes, Medium < 30 minutes, Low < 15 minutes
 * @param {number} disruptionMinutes - Disruption duration in minutes
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateTransportDisruptionsSeverityLevel = (disruptionMinutes, highThreshold = 30, mediumThreshold = 30, lowThreshold = 15) => {
  if (disruptionMinutes === null || disruptionMinutes === undefined || isNaN(disruptionMinutes)) {
    return 'LOW';
  }
  const minutes = Number(disruptionMinutes);
  
  // Handle threshold objects/arrays with operators
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 30, '>');
  const medium = parseThreshold(mediumThreshold, 30, '<');
  const low = parseThreshold(lowThreshold, 15, '<');
  
  if (evaluateOperator(minutes, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(minutes, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for school closures based on total_schools_in_ward (days)
 * Logic: High > 18 days, Medium < 18 days, Low < 5 days
 * @param {number} closureDays - Number of closure days
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateSchoolClosuresSeverityLevel = (closureDays, highThreshold = 18, mediumThreshold = 18, lowThreshold = 5) => {
  if (closureDays === null || closureDays === undefined || isNaN(closureDays)) {
    return 'LOW';
  }
  const days = Number(closureDays);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 18, '>');
  const medium = parseThreshold(mediumThreshold, 18, '<');
  const low = parseThreshold(lowThreshold, 5, '<');
  
  if (evaluateOperator(days, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(days, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for school cooling infrastructure
 * Logic: High > 95%, Medium < 95%, Low < 90%
 * @param {number} percentageWithoutCooling - Percentage without cooling infrastructure (0-100)
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateSchoolCoolingSeverityLevel = (percentageWithoutCooling, highThreshold = 95, mediumThreshold = 95, lowThreshold = 90) => {
  if (percentageWithoutCooling === null || percentageWithoutCooling === undefined || isNaN(percentageWithoutCooling)) {
    return 'LOW';
  }
  const percentage = Number(percentageWithoutCooling);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 95, '>');
  const medium = parseThreshold(mediumThreshold, 95, '<');
  const low = parseThreshold(lowThreshold, 90, '<');
  
  if (evaluateOperator(percentage, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(percentage, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for water supply drop ratio
 * Logic: High > 0.4, Medium < 0.4, Low < 0.1
 * @param {number} dropRatio - Drop ratio value
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateWaterSupplySeverityLevel = (dropRatio, highThreshold = 0.4, mediumThreshold = 0.4, lowThreshold = 0.1) => {
  if (dropRatio === null || dropRatio === undefined || isNaN(dropRatio)) {
    return 'LOW';
  }
  const ratio = Number(dropRatio);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 0.4, '>');
  const medium = parseThreshold(mediumThreshold, 0.4, '<');
  const low = parseThreshold(lowThreshold, 0.1, '<');
  
  if (evaluateOperator(ratio, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(ratio, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for water tankers dependency
 * Logic: High > 0.4, Medium < 0.4, Low < 0.1
 * @param {number} dependencyRatio - Dependency ratio or emergency requests ratio
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateWaterTankersSeverityLevel = (dependencyRatio, highThreshold = 0.4, mediumThreshold = 0.4, lowThreshold = 0.1) => {
  if (dependencyRatio === null || dependencyRatio === undefined || isNaN(dependencyRatio)) {
    return 'LOW';
  }
  const ratio = Number(dependencyRatio);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 0.4, '>');
  const medium = parseThreshold(mediumThreshold, 0.4, '<');
  const low = parseThreshold(lowThreshold, 0.1, '<');
  
  if (evaluateOperator(ratio, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(ratio, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for cooling system failures
 * Logic: High > 45 minutes, Medium < 45 minutes, Low < 5 minutes (if not hospital)
 * @param {number} capacityAffectedMinutes - Cooling capacity affected duration in minutes
 * @param {boolean} isHospital - Whether the affected location is a hospital
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateCoolingSystemFailuresSeverityLevel = (capacityAffectedMinutes, isHospital = false, highThreshold = 45, mediumThreshold = 45, lowThreshold = 5) => {
  if (capacityAffectedMinutes === null || capacityAffectedMinutes === undefined || isNaN(capacityAffectedMinutes)) {
    return 'LOW';
  }
  const minutes = Number(capacityAffectedMinutes);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 45, '>');
  const medium = parseThreshold(mediumThreshold, 45, '<');
  const low = parseThreshold(lowThreshold, 5, '<');
  
  if (evaluateOperator(minutes, high.operator, high.value)) return 'HIGH';
  if (!isHospital && evaluateOperator(minutes, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for traffic signal failures
 * Logic: High > 30 minutes, Medium < 30 minutes, Low < 5 minutes
 * @param {number} durationMinutes - Total duration in minutes
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateTrafficSignalFailuresSeverityLevel = (durationMinutes, highThreshold = 30, mediumThreshold = 30, lowThreshold = 5) => {
  if (durationMinutes === null || durationMinutes === undefined || isNaN(durationMinutes)) {
    return 'LOW';
  }
  const minutes = Number(durationMinutes);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 30, '>');
  const medium = parseThreshold(mediumThreshold, 30, '<');
  const low = parseThreshold(lowThreshold, 5, '<');
  
  if (evaluateOperator(minutes, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(minutes, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for road closures
 * Logic: High > 2 days, Medium < 2 days, Low < 1 day
 * @param {number} closureDays - Number of days closed
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateRoadClosuresSeverityLevel = (closureDays, highThreshold = 2, mediumThreshold = 2, lowThreshold = 1) => {
  if (closureDays === null || closureDays === undefined || isNaN(closureDays)) {
    return 'LOW';
  }
  const days = Number(closureDays);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 2, '>');
  const medium = parseThreshold(mediumThreshold, 2, '<');
  const low = parseThreshold(lowThreshold, 1, '<');
  
  if (evaluateOperator(days, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(days, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for heat differential (urban vs rural)
 * Logic: High > 6 deg C, Medium < 6 deg C, Low < 2 deg C
 * @param {number} tempDifference - Temperature difference in Celsius
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateHeatDifferentialSeverityLevel = (tempDifference, highThreshold = 6, mediumThreshold = 6, lowThreshold = 2) => {
  if (tempDifference === null || tempDifference === undefined || isNaN(tempDifference)) {
    return 'LOW';
  }
  const diff = Number(tempDifference);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 6, '>');
  const medium = parseThreshold(mediumThreshold, 6, '<');
  const low = parseThreshold(lowThreshold, 2, '<');
  
  if (evaluateOperator(diff, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(diff, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

/**
 * Calculate severity level for surface temperature & vegetation index
 * Uses dual thresholds: NDVI and LST (Land Surface Temperature)
 * Logic: 
 *   High: NDVI < 0.1 AND LST > 45
 *   Medium: NDVI > 0.1 AND LST < 45
 *   Low: NDVI > 0.5 AND LST < 40
 * @param {number} ndvi - NDVI value
 * @param {number} lst - LST value in Celsius
 * @param {number|Object|Array} highNdviThreshold - High NDVI threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} highLstThreshold - High LST threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumNdviThreshold - Medium NDVI threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumLstThreshold - Medium LST threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowNdviThreshold - Low NDVI threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowLstThreshold - Low LST threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateSurfaceTemperatureSeverityLevel = (
  ndvi, 
  lst, 
  highNdviThreshold = 0.1, 
  highLstThreshold = 45,
  mediumNdviThreshold = 0.1,
  mediumLstThreshold = 45,
  lowNdviThreshold = 0.5,
  lowLstThreshold = 40
) => {
  if (ndvi === null || ndvi === undefined || isNaN(ndvi) || lst === null || lst === undefined || isNaN(lst)) {
    return 'LOW';
  }
  const ndviVal = Number(ndvi);
  const lstVal = Number(lst);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const highNdvi = parseThreshold(highNdviThreshold, 0.1, '<');
  const highLst = parseThreshold(highLstThreshold, 45, '>');
  const mediumNdvi = parseThreshold(mediumNdviThreshold, 0.1, '>');
  const mediumLst = parseThreshold(mediumLstThreshold, 45, '<');
  const lowNdvi = parseThreshold(lowNdviThreshold, 0.5, '>');
  const lowLst = parseThreshold(lowLstThreshold, 40, '<');
  
  // High: NDVI < 0.1 AND LST > 45
  if (evaluateOperator(ndviVal, highNdvi.operator, highNdvi.value) && evaluateOperator(lstVal, highLst.operator, highLst.value)) {
    return 'HIGH';
  }
  
  // Low: NDVI > 0.5 AND LST < 40
  if (evaluateOperator(ndviVal, lowNdvi.operator, lowNdvi.value) && evaluateOperator(lstVal, lowLst.operator, lowLst.value)) {
    return 'LOW';
  }
  
  // Medium: NDVI > 0.1 AND LST < 45 (or other cases)
  return 'MEDIUM';
};

/**
 * Calculate severity level for shelter occupancy
 * Logic: High > 40%, Medium < 40%, Low < 10%
 * @param {number} occupancyRate - Occupancy rate percentage (0-100)
 * @param {number|Object|Array} highThreshold - High threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} mediumThreshold - Medium threshold value, object with {value, operator}, or array [value, operator]
 * @param {number|Object|Array} lowThreshold - Low threshold value, object with {value, operator}, or array [value, operator]
 * @returns {string} - Severity level: 'HIGH', 'MEDIUM', or 'LOW'
 */
export const calculateShelterOccupancySeverityLevel = (occupancyRate, highThreshold = 40, mediumThreshold = 40, lowThreshold = 10) => {
  if (occupancyRate === null || occupancyRate === undefined || isNaN(occupancyRate)) {
    return 'LOW';
  }
  const rate = Number(occupancyRate);
  
  const parseThreshold = (threshold, defaultVal, defaultOp) => {
    if (Array.isArray(threshold) && threshold.length >= 2) {
      return { value: threshold[0], operator: threshold[1] };
    } else if (typeof threshold === 'object' && threshold !== null && threshold.value !== undefined) {
      return { value: threshold.value, operator: threshold.operator || defaultOp };
    }
    return { value: threshold || defaultVal, operator: defaultOp };
  };
  
  const high = parseThreshold(highThreshold, 40, '>');
  const medium = parseThreshold(mediumThreshold, 40, '<');
  const low = parseThreshold(lowThreshold, 10, '<');
  
  if (evaluateOperator(rate, high.operator, high.value)) return 'HIGH';
  if (evaluateOperator(rate, low.operator, low.value)) return 'LOW';
  return 'MEDIUM';
};

